'use client';

import React from 'react';
import PropTypes from 'prop-types';

import { Box, Card, Chip, Stack, Divider, Checkbox, Typography } from '@mui/material';

import { getPlatformInfo } from 'src/utils/platform/platform-info';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';

import { RADIUS } from 'src/theme/ui-tokens';
import { languageMap } from 'src/constants/languages';

import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import PostMediaBadge from 'src/components/cmui/post-card/PostMediaBadge';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';

import PostKindChips, { getPostKindFlags } from './PostKindChips';


const MAX_VISIBLE_BRAND_CHIPS = 3;

/**
 * 가로 형태의 게시물 카드 컴포넌트 (리스트 뷰용)
 * 
 * ⚠️ `@param` 을 구조분해 인자에 **접두 없이** 적으면 TS 가 첫 인자 자체의 타입으로
 * 읽는다 — 그러면 props 가 `post` 의 모양이 되어 **어떤 프롭도 못 넘긴다.**
 * `@param {Object} props` + `props.x` 꼴로 적는다. 표본은 같은 폴더의 `PostDetail.tsx`.
 *
 * @param {Object} props
 * @param {Object} props.post - 게시물 데이터
 * @param {Function} [props.onClick] - 카드 클릭 핸들러
 * @param {string|string[]} [props.searchKeyword] - 검색 키워드
 * @param {string|string[]} [props.clientSearch] - 클라이언트 검색어
 * @param {Array<string>} [props.topicKeywords] - 토픽 키워드 배열
 * @param {Object} [props.expandedSelectedBrands] - 선택된 브랜드 정보
 * @param {'right'|'left'} [props.imagePosition]
 * @param {number} [props.index] - 아이템 인덱스
 * @param {boolean} [props.external]
 * @param {boolean} [props.showKindChips]
 * @param {boolean} [props.hidePlatformIcon]
 * @param {boolean} [props.showBookmarkButton]
 * @param {boolean} [props.enableCampaignAdd]
 * @param {boolean} [props.enableImageBookmark]
 * @param {Object} [props.bookmarkTargetPost]
 * @param {boolean} [props.isBookmark]
 * @param {Set<string>} [props.selectedPostIds]
 * @param {Function} [props.onToggleSelect]
 * @returns {any}
 */
function PostCardList({
  post,
  onClick,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  expandedSelectedBrands,
  imagePosition = 'right', // 'right' | 'left'
  index = 0, // 아이템 인덱스
  external = false,
  showKindChips = true,
  hidePlatformIcon,
  showBookmarkButton = true,
  enableCampaignAdd = false,
  enableImageBookmark = false,
  bookmarkTargetPost,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
  actionSlot,
}: {
  post: any
  onClick?: (post: any) => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  /** ⚠️ `Set` 도 배열도 온다 — 코드가 `.has` 유무로 오리 타이핑한다 */
  expandedSelectedBrands?: any
  imagePosition?: 'left' | 'right'
  index?: number
  external?: boolean
  showKindChips?: boolean
  hidePlatformIcon?: boolean
  showBookmarkButton?: boolean
  enableCampaignAdd?: boolean
  enableImageBookmark?: boolean
  bookmarkTargetPost?: any
  /**
   * 줄 **발치의 동작 묶음에 끼워 넣을 것** — 북마크 옆에 나란히 선다.
   * 🔑 부르는 화면이 제 단추를 줄 «밖»에 덧대지 않게 연 자리다 — 덧대면 줄이 제 배경을 가지는
   *   부품이라 이음매가 생기고, 아이콘만 딴 데 떠 있게 된다.
   * ⚠️ 여기 오는 것은 **아이콘 한 칸**이다 — 글자 단추를 넣으면 발치 줄이 무너진다.
   */
  actionSlot?: React.ReactNode
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<string>
  onToggleSelect?: (...args: any[]) => void
}) {
  const {
    title,
    content,
    author,
    source,
    imageUrl,
    imageS3Url,
    date,
    timestamp,
    market,
    canonicalBrands,
    brands = [],
  } = post;

  // canonicalBrand 필드가 존재하면 사용 (빈 배열이어도 사용), 없으면 기존 brands 필드 사용
  const displayBrands = canonicalBrands !== undefined
    ? (Array.isArray(canonicalBrands) ? canonicalBrands : [])
    : (Array.isArray(brands) ? brands : []);

  // sentiment 데이터 직접 추출 (구조 분해 할당 대신)
  const sentimentEvidencePositive = post?.sentimentEvidencePositive || [];
  const sentimentEvidenceNegative = readNegativeEvidence(post);

  const isHit = Boolean(post?.isHit);
  // isQuoted는 post에서 가져옴
  const isQuoteValue = Boolean(post?.isQuote);
  // isRepost는 post에서 가져옴
  const isRepostValue = Boolean(post?.isRepost);
  const kindChipFlags = getPostKindFlags(post);

  // 플랫폼 정보 utils로 추출
  const platformInfo = getPlatformInfo(source);
  const platformIconPath = source ? `/assets/icons/platforms/${String(source).toLowerCase()}.svg` : null;

  // external prop이 true인 경우 (PostCardListRepost 안에 있는 PostCardList) 플랫폼 아이콘 숨김
  // 단, 원문 카드에서도 채널 아이콘을 보여야 하는 케이스가 있어 override 가능
  const shouldHidePlatformIcon = hidePlatformIcon ?? external;

  const formatTimestampFn = formatTimestamp;

  const latestMetrics = getMetricsByScoreTime(post);

  // 이미지 우선순위: imageS3Url > imageUrl > null
  const imageSrc = imageS3Url || imageUrl || null;

  // 게시물 상태에 따른 UI 조정
  const hasImage = !!imageSrc;
  const hasTitle = !!title
  const hasContent = !!content
  // 인용/리포 원문 등 동일 문자열을 title·content 양쪽에 넣은 경우 본문 중복 렌더 방지
  const trimmedTitle = String(title ?? '').trim()
  const trimmedContent = String(content ?? '').trim()
  const shouldSkipDuplicateBody =
    hasTitle && hasContent && trimmedTitle.length > 0 && trimmedTitle === trimmedContent
  const showContentBlock = hasContent && !shouldSkipDuplicateBody

  // 텍스트 하이라이트: sentiment evidence 포함
  const highlightText = (text: unknown, searchKw?: unknown, topicKws?: unknown) =>
    highlightTextWithHtmlRemoval(
      text as string,
      searchKw as string,
      topicKws as string[],
      clientSearch,
      sentimentEvidencePositive,
      sentimentEvidenceNegative
    );

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: string | null | undefined, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={searchKeyword as string}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearch}
      sentimentPositive={sentimentEvidencePositive}
      sentimentNegative={sentimentEvidenceNegative}
    />
  );

  // market 코드 → 표시 이름 매핑 (PostCard와 동일)
  const marketDisplay = React.useMemo(() => {
    if (!market) return '';
    const code = String(market).toLowerCase();
    const found = Array.isArray(languageMap)
      ? languageMap.find((l) => String(l.code).toLowerCase() === code)
      : null;
    return found?.name || market;
  }, [market]);

  const timeDisplay = React.useMemo(() => {
    // timestamp 우선, 없으면 date 사용
    if (timestamp) return formatTimestampFn(timestamp);
    if (date) return formatTimestampFn(date);
    return '';
  }, [timestamp, date, formatTimestampFn]);

  return (
    <Card
      sx={{
        borderRadius: external ? 1 : 0,
        bgcolor: 'white',
        border: 'none',
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative',
        py: external ? 1 : 2,
        ...(external ? { px: 1 } : {}),
        width: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          bgcolor: 'primary.lighter',
          transition: 'all 0.2s ease-in-out',
        } : {},
      }}
      onClick={() => onClick && onClick(post)}
    >
      <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch" sx={{ width: '100%' }}>

        {/* 이미지가 왼쪽 정렬일 때 이미지 컬럼을 먼저 렌더 */}
        {hasImage && imagePosition === 'left' && (
          <Box sx={{ width: { xs: 90, sm: 120 }, mr: { xs: 0, sm: 0 }, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pb: 2 }}>
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: RADIUS.sm, overflow: 'hidden' }}>
              <Box
                component="img"
                src={imageSrc}
                alt="post media"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <PostMediaBadge post={post} iconOnly />
            </Box>
          </Box>
        )}

        {/* 중앙: 컨텐츠 영역 */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>

          {showKindChips && (
            <PostKindChips
              post={post}
              flags={kindChipFlags}
              stackSx={{ mb: { xs: 0.75, sm: 1 } }}
            />
          )}


          {/* 헤더 정보 (한 줄) */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 0.75, sm: 1 }} mb={{ xs: 0.75, sm: 1 }} sx={{ width: '100%', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
            {/* 북마크 모드: 날짜 행 왼쪽 끝에 체크박스 */}
            {isBookmark && onToggleSelect && (
              <Stack sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={selectedPostIds?.has?.(bookmarkTargetPost?.id ?? post?.id) ?? false}
                  onChange={() => onToggleSelect(bookmarkTargetPost?.id ?? post?.id)}
                  sx={{ p: 0 }}
                  aria-label={`${post?.title ?? '게시물'} 선택`}
                />
              </Stack>
            )}
            {/* 좌측: 플랫폼 아이콘 + 작성자/계정/마켓 + Hit 배지 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, overflow: 'hidden' }}>
              {platformIconPath && !shouldHidePlatformIcon && (
                <Box
                  component="img"
                  src={platformIconPath}
                  alt={platformInfo?.label || source}
                  sx={{ width: 18, height: 18, flexShrink: 0 }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                {author && (
                  <Typography
                    sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                  >
                    {renderIdentity(`${author || ''}`)}
                  </Typography>
                )}
                {post?.account && (
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '12px', sm: '13.5px' },
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      flexShrink: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: { xs: '75%', sm: '60%' },
                    }}
                    title={`@${post.account}`}
                  >
                    {renderIdentity(`${post?.account || ''}`, '@')}
                  </Typography>
                )}
              </Box>
              {marketDisplay && (
                <Typography color="text.secondary" variant="caption" sx={{ fontSize: { xs: '11px', sm: '12px' }, whiteSpace: 'nowrap' }}>
                  {marketDisplay}
                </Typography>
              )}
              {isHit && (
                <Box sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: RADIUS.sm,
                  bgcolor: 'error.lighter',
                  color: 'error.dark',
                  fontSize: '11px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}>
                  HIT
                </Box>
              )}
            </Box>
            {/* 날짜는 카드 우하단에 공통 렌더링으로 이동 */}
          </Stack>

          {/* 제목/내용 */}
          <Stack direction="row" alignItems="flex-start" spacing={{ xs: 1, sm: 1.5 }} sx={{ width: '100%', maxWidth: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {hasTitle && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: showContentBlock ? 1 : 0,
                    color: 'black',
                    display: '-webkit-box',
                    WebkitLineClamp: showContentBlock ? 1 : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    fontSize: { xs: '13.5px', sm: '15px' },
                    // fontWeight: 'Bold',
                    lineHeight: { xs: 1.35, sm: 1.4 },
                    textAlign: 'left',
                  }}
                >
                  <SafeHtml html={highlightText(title, searchKeyword, topicKeywords)} />
                </Typography>
              )}
              {showContentBlock && !external && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0,
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    lineHeight: { xs: 1.35, sm: 1.4 },
                    fontSize: { xs: '12px', sm: '13.5px' },
                    // fontWeight: 'Light',
                    textAlign: 'left',
                  }}
                >
                  <SafeHtml html={highlightText(content, searchKeyword, topicKeywords)} />
                </Typography>
              )}
            </Box>
          </Stack>

          {/* 메트릭 칩(상단) + 브랜드 칩(하단) - 모바일에서 세로 정렬, 데스크톱에서 가로 정렬 */}
          {(displayBrands.length > 0) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mt: hasImage ? 'auto' : '0', pt: { xs: 1, sm: 2 }, width: '100%' }}
            >
              {/* 브랜드 칩: 모바일에선 하단 배치 */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  order: { xs: 2, sm: 1 },
                  mt: { xs: 1, sm: 0 },
                  width: '100%',
                }}
              >
                {displayBrands.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="nowrap"
                    alignItems="center"
                    sx={{ overflow: 'hidden' }}
                  >
                    {displayBrands.slice(0, MAX_VISIBLE_BRAND_CHIPS).map((brand, i) => {
                      const isSelected = expandedSelectedBrands && expandedSelectedBrands.has &&
                        Array.from<any>(expandedSelectedBrands).some(selectedBrand =>
                          selectedBrand.toLowerCase() === brand.toLowerCase()
                        );
                      return (
                        <Chip
                          key={`brand-${brand}-${i}`}
                          label={brand}
                          size="small"
                          sx={{
                            minHeight: 24,
                            height: 24,
                            fontSize: '11px',
                            fontWeight: 'Medium',
                            bgcolor: isSelected ? '#fbbf24' : 'primary.lighter',
                            color: isSelected ? '#78350f' : 'primary.dark',
                            border: isSelected ? '1px solid #f59e1a' : 'none',
                            borderRadius: RADIUS.pill,
                            '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        />
                      );
                    })}
                    {displayBrands.length > MAX_VISIBLE_BRAND_CHIPS && (
                      <Typography color="primary.dark" variant="caption" sx={{ flexShrink: 0 }}>
                        {`+${displayBrands.length - MAX_VISIBLE_BRAND_CHIPS}`}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>

            </Stack>
          )}
        </Box>

        {/* 오른쪽(기본): 1:1 이미지 (이미지가 있을 때만 컬럼 확보) */}
        {hasImage && imagePosition !== 'left' && (
          <Box sx={{ width: { xs: 90, sm: 140 }, ml: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: RADIUS.sm, overflow: 'hidden' }}>
              <Box
                component="img"
                src={imageSrc}
                alt="post media"
                onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <PostMediaBadge post={post} iconOnly />
            </Box>
          </Box>
        )}
      </Stack>

      {/* 리포스트/인용 카드 내부 원문(external)은 메타(수치칩/날짜/북마크) 미표시 */}
      {!external && (
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          rowGap={0.5}
          justifyContent="space-between"
          mt={1}
          sx={{
            bottom: hasImage ? 16 : 8,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {!isQuoteValue && (
            // 캡처 표식 `post-metrics` — 메일 캡처는 반응 수치가 하나도 없는 글이면 이 줄을 찍는 동안 걷는다
            <Stack
              data-capture="post-metrics"
              direction="row"
              spacing={1}
              alignItems="center"
            >
              {/* 좋아요 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: { xs: 22, sm: 26 },
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: { xs: 1, sm: 1.25 },
                  py: 0,
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/like.svg"
                  sx={{
                    width: { xs: 12, sm: 14 },
                    height: { xs: 12, sm: 14 },
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: { xs: '12px', sm: '13.5px' },
                  }}
                >
                  {formatNumber(latestMetrics.likeCount)}
                </Typography>
              </Box>

              {/* 댓글 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: { xs: 22, sm: 26 },
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: { xs: 1, sm: 1.25 },
                  py: 0,
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/reply.svg"
                  sx={{
                    width: { xs: 12, sm: 14 },
                    height: { xs: 12, sm: 14 },
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: { xs: '12px', sm: '13.5px' },
                  }}
                >
                  {formatNumber(latestMetrics.commentCount)}
                </Typography>
              </Box>

              {/* 조회수 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: { xs: 22, sm: 26 },
                  bgcolor: 'grey.100',
                  borderRadius: RADIUS.pill,
                  px: { xs: 1, sm: 1.25 },
                  py: 0,
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/icon_svg/view.svg"
                  sx={{
                    width: { xs: 12, sm: 14 },
                    height: { xs: 12, sm: 14 },
                    filter: 'brightness(0) saturate(100%)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'black',
                    fontWeight: 'Medium',
                    fontSize: { xs: '12px', sm: '13.5px' },
                  }}
                >
                  {post?.contentsType === 'instagram_post' && latestMetrics.viewCount === 0
                    ? '-'
                    : formatNumber(latestMetrics.viewCount)}
                </Typography>
              </Box>
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            {date && timeDisplay && (
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  fontSize: { xs: '11px', sm: '12px' },
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {timeDisplay}
              </Typography>
            )}

            {!isBookmark && (showBookmarkButton || enableCampaignAdd || actionSlot) && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
                sx={{ pointerEvents: 'auto', flexShrink: 0 }}
              >
                {actionSlot}
                {showBookmarkButton && (
                  <PostCardBookmarkOverlay
                    placement="postList"
                    post={bookmarkTargetPost ?? post}
                  />
                )}
                {enableCampaignAdd && (
                  <PostCardCampaignOverlay
                    placement="postList"
                    post={bookmarkTargetPost ?? post}
                  />
                )}
              </Stack>
            )}
          </Stack>

        </Stack>
      )}

      {!isQuoteValue && !isRepostValue && (
        <Divider
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            borderColor: 'rgba(0, 0, 0, 0.1)',
          }}
        />
      )}
    </Card>
  );
}

PostCardList.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    account: PropTypes.string,
    source: PropTypes.string,
    imageUrl: PropTypes.string,
    imageS3Url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    market: PropTypes.string,
    indexedAt: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    canonicalBrands: PropTypes.arrayOf(PropTypes.string),
    likeCount: PropTypes.number,
    commentCount: PropTypes.number,
    viewCount: PropTypes.number,
    ingredients: PropTypes.arrayOf(PropTypes.string),
    concerns: PropTypes.arrayOf(PropTypes.string),
    persona_age: PropTypes.string,
    persona_gender: PropTypes.string,
    sentimentEvidencePositive: PropTypes.arrayOf(PropTypes.string),
    sentimentEvidenceNegative: PropTypes.arrayOf(PropTypes.string),
    metrics: PropTypes.objectOf(PropTypes.shape({
      likeCount: PropTypes.number,
      commentCount: PropTypes.number,
      viewCount: PropTypes.number,
      requestDateTime: PropTypes.string,
    })),
  }).isRequired,
  onClick: PropTypes.func,
  searchKeyword: PropTypes.string,
  clientSearch: PropTypes.string,
  topicKeywords: PropTypes.arrayOf(PropTypes.string),
  expandedSelectedBrands: PropTypes.objectOf(PropTypes.bool),
  imagePosition: PropTypes.oneOf(['left', 'right']),
  index: PropTypes.number,
  external: PropTypes.bool,
  enableImageBookmark: PropTypes.bool,
  enableCampaignAdd: PropTypes.bool,
  bookmarkTargetPost: PropTypes.object,
};

export default React.memo(PostCardList); 